import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { X, Search, Mail, Phone, Loader, UserX } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { apiCall } from '../../services/apiUtil';

/**
 * InviteModal - Generic reusable modal for searching and inviting members.
 *
 * Props:
 *   isOpen               boolean
 *   onClose              () => void
 *   title                string  (default: 'Add or Invite Members')
 *   staffRoles           string[] | null  — enables per-result role dropdown
 *   checkIfAlreadyExists (userId: string) => boolean  — marks already-added users
 *   onAddExistingMember  async (user, role?) => void  — caller adds user + shows success toast
 *   onInviteByEmail      async ({ firstName, lastName, email }) => void
 *   onInviteByPhone      async ({ firstName, lastName, phone, countryCode }) => void
 *   invitationId         string | null  — pre-fills form from existing invitation
 *   reinviteUserId       string | null  — shows re-invite banner for this user
 */
const InviteModal = ({
  isOpen,
  onClose,
  title = 'Add or Invite Members',
  staffRoles = null,
  checkIfAlreadyExists = null,
  onAddExistingMember,
  onInviteByEmail,
  onInviteByPhone,
  invitationId = null,
  reinviteUserId = null,
}) => {
  const { theme } = useTheme();

  const [activeMode, setActiveMode] = useState('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [addingMemberId, setAddingMemberId] = useState(null);
  const [reinviteUser, setReinviteUser] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState({});
  const [customRoles, setCustomRoles]     = useState({});

  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [inviting, setInviting] = useState(false);

  // Pre-fill form when editing an existing invitation
  useEffect(() => {
    if (!isOpen || !invitationId) return;
    apiCall(`/invitations/${invitationId}`)
      .then(inv => {
        if (!inv) return;
        setInviteFirstName(inv.firstName || '');
        setInviteLastName(inv.lastName || '');
        if (inv.emailid) { setInviteEmail(inv.emailid); setActiveMode('email'); }
        else if (inv.phoneNumber) { setInvitePhone(inv.phoneNumber.replace(/^\+\d+/, '')); setActiveMode('phone'); }
      })
      .catch(err => console.error('Failed to load invitation:', err));
  }, [isOpen, invitationId]);

  // Load user for re-invite banner
  useEffect(() => {
    if (!isOpen || !reinviteUserId) { setReinviteUser(null); return; }
    apiCall(`/user/${reinviteUserId}`)
      .then(user => { setReinviteUser(user); setActiveMode('search'); })
      .catch(err => console.error('Failed to load user:', err));
  }, [isOpen, reinviteUserId]);

  // Body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isResend = !!invitationId;
  const getRole = (userId) => {
    const sel = selectedRoles[userId] ?? staffRoles?.[0] ?? null;
    if (sel === '__custom__') return (customRoles[userId] || '').trim() || null;
    return sel;
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 4) return;
    setSearchLoading(true);
    try {
      const result = await apiCall(`/user?search=${encodeURIComponent(searchQuery.trim())}&page=1&limit=10`);
      setSearchResults(result.data || []);
      setHasMore((result.data || []).length === 10);
      setCurrentPage(1);
    } catch {
      toast.error('Failed to search users');
    }
    setSearchLoading(false);
  };

  const handleLoadMore = async () => {
    setSearchLoading(true);
    try {
      const nextPage = currentPage + 1;
      const result = await apiCall(`/user?search=${encodeURIComponent(searchQuery.trim())}&page=${nextPage}&limit=10`);
      const users = result.data || [];
      setSearchResults(prev => [...prev, ...users]);
      setHasMore(users.length === 10);
      setCurrentPage(nextPage);
    } catch {
      toast.error('Failed to load more users');
    }
    setSearchLoading(false);
  };

  const handleAddMember = async (user) => {
    setAddingMemberId(user.id);
    try {
      await onAddExistingMember(user, getRole(user.id));
      setSearchResults(prev => prev.filter(u => u.id !== user.id));
    } catch (err) {
      toast.error(err?.message || 'Failed to add member');
    }
    setAddingMemberId(null);
  };

  const handleInviteEmail = async () => {
    if (!inviteFirstName.trim() || !inviteLastName.trim() || !inviteEmail.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setInviting(true);
    try {
      await onInviteByEmail({
        firstName: inviteFirstName.trim(),
        lastName: inviteLastName.trim(),
        email: inviteEmail.trim(),
      });
      setInviteEmail('');
      setInviteFirstName('');
      setInviteLastName('');
    } catch (err) {
      toast.error(err?.message || 'Failed to send invitation');
    }
    setInviting(false);
  };

  const handleInvitePhone = async () => {
    if (!inviteFirstName.trim() || !inviteLastName.trim() || !invitePhone.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    if (!/^\d{10}$/.test(invitePhone.replace(/\D/g, ''))) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    setInviting(true);
    try {
      await onInviteByPhone({
        firstName: inviteFirstName.trim(),
        lastName: inviteLastName.trim(),
        phone: invitePhone.trim(),
        countryCode,
      });
      setInvitePhone('');
      setInviteFirstName('');
      setInviteLastName('');
    } catch (err) {
      toast.error(err?.message || 'Failed to send invitation');
    }
    setInviting(false);
  };

  const handleClose = () => {
    setSearchQuery('');
    setSearchResults([]);
    setReinviteUser(null);
    setInviteEmail('');
    setInvitePhone('');
    setInviteFirstName('');
    setInviteLastName('');
    setSelectedRoles({});
    setCustomRoles({});
    setActiveMode('search');
    onClose();
  };

  const countryCodes = [
    { code: '+1',  flag: '🇺🇸', label: 'US / Canada' },
    { code: '+44', flag: '🇬🇧', label: 'United Kingdom' },
    { code: '+91', flag: '🇮🇳', label: 'India' },
    { code: '+86', flag: '🇨🇳', label: 'China' },
    { code: '+81', flag: '🇯🇵', label: 'Japan' },
    { code: '+49', flag: '🇩🇪', label: 'Germany' },
    { code: '+33', flag: '🇫🇷', label: 'France' },
    { code: '+61', flag: '🇦🇺', label: 'Australia' },
    { code: '+55', flag: '🇧🇷', label: 'Brazil' },
  ];

  const inputCls = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-current focus:border-transparent ${theme.input} ${theme.border}`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className={`${theme.card} rounded-lg max-w-2xl w-full h-[80vh] max-h-[600px] overflow-hidden flex flex-col my-auto`}>

        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${theme.border}`}>
          <h3 className={`text-lg font-semibold ${theme.text}`}>
            {isResend ? 'Resend Invitation' : title}
          </h3>
          <button onClick={handleClose} aria-label="Close" title="Close" className={`p-2 ${theme.cardHover} rounded-full transition-colors`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab switcher */}
        <div className={`p-4 border-b ${theme.border}`}>
          <div className="flex gap-x-2">
            {[
              { key: 'search', icon: Search, label: 'Search' },
              { key: 'email',  icon: Mail,   label: 'Invite by Email' },
              { key: 'phone',  icon: Phone,  label: 'Invite by Phone' },
            ].map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                onClick={() => setActiveMode(key)}
                className={`flex-1 px-3 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-1.5 text-sm ${
                  activeMode === key
                    ? theme.tabActive
                    : `${theme.backgroundSecondary} ${theme.text}`
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-4 flex-1 overflow-y-auto min-h-0">

          {/* ── Search tab ── */}
          {activeMode === 'search' && (
            <div className="h-full flex flex-col">
              <div className="relative mb-4">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  placeholder="Enter at least 4 characters to search..."
                  className={inputCls}
                />
                <button
                  onClick={handleSearch}
                  disabled={searchLoading || searchQuery.trim().length < 4}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md transition-colors ${
                    searchQuery.trim().length >= 4 && !searchLoading
                      ? `${theme.link} hover:bg-blue-50 cursor-pointer`
                      : 'text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {searchLoading
                    ? <Loader className="w-4 h-4 animate-spin" />
                    : <Search className="w-4 h-4" />}
                </button>
              </div>

              <div className="flex-1 overflow-y-auto space-y-2">
                {/* Re-invite banner */}
                {reinviteUser && (
                  <div className={`mb-2 p-3 rounded-lg border-2 border-orange-300 ${theme.backgroundSecondary}`}>
                    <p className="text-xs font-medium text-orange-600 mb-2">Invited member — send a reminder</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-x-3">
                        {reinviteUser.profilePicture
                          ? <img src={reinviteUser.profilePicture} alt="" className="w-10 h-10 rounded-full object-cover" />
                          : (
                            <div className={`w-10 h-10 ${theme.gradient} rounded-full flex items-center justify-center`}>
                              <span className="text-lg font-bold text-white">
                                {reinviteUser.firstName?.charAt(0)?.toUpperCase() || 'U'}
                              </span>
                            </div>
                          )}
                        <div>
                          <p className={`font-medium ${theme.text}`}>{reinviteUser.firstName} {reinviteUser.lastName}</p>
                          <p className={`text-sm ${theme.textMuted}`}>{reinviteUser.email}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleAddMember(reinviteUser)}
                        disabled={addingMemberId !== null}
                        className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
                      >
                        {addingMemberId === reinviteUser.id ? 'Sending...' : 'Re-invite'}
                      </button>
                    </div>
                  </div>
                )}

                {searchLoading && searchResults.length === 0 ? (
                  <div className="flex justify-center py-8">
                    <Loader className={`w-6 h-6 animate-spin ${theme.link}`} />
                  </div>
                ) : searchResults.length > 0 ? (
                  <>
                    {searchResults.map(user => {
                      const alreadyAdded = checkIfAlreadyExists ? checkIfAlreadyExists(String(user.id)) : false;
                      return (
                        <div key={user.id} className={`flex items-center justify-between p-3 rounded-lg ${theme.backgroundSecondary}`}>
                          <div className="flex items-center gap-x-3 min-w-0 flex-1">
                            {user.profilePicture
                              ? <img src={user.profilePicture} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                              : (
                                <div className={`w-10 h-10 ${theme.gradient} rounded-full flex items-center justify-center flex-shrink-0`}>
                                  <span className="text-lg font-bold text-white">
                                    {user.firstName?.charAt(0)?.toUpperCase() || 'U'}
                                  </span>
                                </div>
                              )}
                            <div className="min-w-0">
                              <p className={`font-medium ${theme.text} truncate`}>{user.firstName} {user.lastName}</p>
                              <p className={`text-sm ${theme.textMuted} truncate`}>{user.email}</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            {staffRoles && !alreadyAdded && (
                              <div className="flex flex-col gap-1">
                                <select
                                  value={selectedRoles[user.id] ?? staffRoles[0]}
                                  onChange={e => setSelectedRoles(prev => ({ ...prev, [user.id]: e.target.value }))}
                                  className={`text-sm px-2 py-1.5 border rounded-lg ${theme.input} focus:ring-2 focus:ring-current`}
                                >
                                  {staffRoles.map(r => (
                                    <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                                  ))}
                                  <option value="__custom__">Custom…</option>
                                </select>
                                {(selectedRoles[user.id] ?? staffRoles[0]) === '__custom__' && (
                                  <input
                                    type="text"
                                    value={customRoles[user.id] || ''}
                                    onChange={e => setCustomRoles(prev => ({ ...prev, [user.id]: e.target.value }))}
                                    placeholder="Enter role name"
                                    className={`text-sm px-2 py-1.5 border rounded-lg ${theme.input} focus:ring-2 focus:ring-current w-32`}
                                    autoFocus
                                  />
                                )}
                              </div>
                            )}

                            {alreadyAdded ? (
                              <span className={`px-3 py-1.5 text-sm rounded-lg ${theme.backgroundSecondary} ${theme.textMuted} border ${theme.border}`}>
                                Already added
                              </span>
                            ) : (
                              <button
                                onClick={() => handleAddMember(user)}
                                disabled={addingMemberId !== null}
                                className={`px-4 py-2 text-sm ${theme.buttonPrimary} rounded-lg  transition-colors disabled:opacity-50`}
                              >
                                {addingMemberId === user.id ? 'Adding...' : 'Add'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {hasMore && (
                      <div className="text-center pt-2">
                        <button
                          onClick={handleLoadMore}
                          disabled={searchLoading}
                          className={`px-6 py-2 text-sm font-medium ${theme.link} ${theme.linkHover} hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50`}
                        >
                          {searchLoading ? 'Loading...' : 'View More'}
                        </button>
                      </div>
                    )}
                  </>
                ) : searchQuery.trim() && !searchLoading ? (
                  <div className="text-center py-8">
                    <UserX className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className={theme.textMuted}>No users found</p>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Search className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className={theme.textMuted}>Enter at least 4 characters and press Search</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Email invite tab ── */}
          {activeMode === 'email' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-sm font-medium ${theme.text} mb-1`}>First Name</label>
                  <input type="text" value={inviteFirstName} onChange={e => setInviteFirstName(e.target.value)} placeholder="First name" className={inputCls} />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${theme.text} mb-1`}>Last Name</label>
                  <input type="text" value={inviteLastName} onChange={e => setInviteLastName(e.target.value)} placeholder="Last name" className={inputCls} />
                </div>
              </div>
              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-1`}>Email Address</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleInviteEmail()}
                  placeholder="Enter email address"
                  className={inputCls}
                />
                <p className={`text-xs ${theme.textMuted} mt-1`}>An invitation will be sent to this address</p>
              </div>
              <button
                onClick={handleInviteEmail}
                disabled={inviting}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 ${theme.buttonPrimary} rounded-lg  transition-colors disabled:opacity-60 font-medium`}
              >
                <Mail className="w-4 h-4" />
                {inviting ? 'Sending...' : isResend ? 'Resend Invitation' : 'Send Invitation'}
              </button>
            </div>
          )}

          {/* ── Phone invite tab ── */}
          {activeMode === 'phone' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-sm font-medium ${theme.text} mb-1`}>First Name</label>
                  <input type="text" value={inviteFirstName} onChange={e => setInviteFirstName(e.target.value)} placeholder="First name" className={inputCls} />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${theme.text} mb-1`}>Last Name</label>
                  <input type="text" value={inviteLastName} onChange={e => setInviteLastName(e.target.value)} placeholder="Last name" className={inputCls} />
                </div>
              </div>
              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-1`}>Country Code</label>
                <select value={countryCode} onChange={e => setCountryCode(e.target.value)} className={inputCls}>
                  {countryCodes.map((c, i) => (
                    <option key={i} value={c.code}>{c.flag} {c.label} ({c.code})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-1`}>Phone Number</label>
                <div className="flex gap-2">
                  <div className={`px-4 py-2 border rounded-lg ${theme.backgroundSecondary} ${theme.border} flex items-center min-w-[64px] justify-center`}>
                    <span className={`font-medium ${theme.text}`}>{countryCode}</span>
                  </div>
                  <input
                    type="tel"
                    value={invitePhone}
                    onChange={e => setInvitePhone(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={e => e.key === 'Enter' && handleInvitePhone()}
                    placeholder="1234567890"
                    maxLength="10"
                    className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-current focus:border-transparent ${theme.input} ${theme.border}`}
                  />
                </div>
                <p className={`text-xs ${theme.textMuted} mt-1`}>10-digit number without country code</p>
              </div>
              <button
                onClick={handleInvitePhone}
                disabled={inviting}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 ${theme.buttonPrimary} rounded-lg  transition-colors disabled:opacity-60 font-medium`}
              >
                <Phone className="w-4 h-4" />
                {inviting ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InviteModal;
